import { Flex, IconButton, Text } from "@radix-ui/themes";
import { DropdownMenu } from "@/components/shared/dropdown-menu";

import cx from "classnames";
import {
  TrackerFieldType,
  TrackerColumn,
  TrackerItem,
  trackerFieldTypeToExploreColumnType,
} from "@/v2/entities/tracker/Tracker.model";
import { SimulatorChannelIcon } from "@/routes/simulators/SimulatorChannelIcon";
import { useCallback, useMemo } from "react";
import { TrackerStageIcon } from "@/v2/assets/mor-shapes/TrackerStageIcon";
import { DotsHorizontalIcon } from "@radix-ui/react-icons";
import { Sources, useTrackerApi } from "@/v2/entities/tracker/Tracker.api";
import { useMutation } from "react-query";
import { useDraggable } from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import { motion } from "framer-motion";
import { Overview } from "@/v2/features/interaction-view/overview/Overview";

function TrackerItemCardTitle({ item }: { item: TrackerItem }) {
  return (
    <Text size={"2"} weight={"medium"} highContrast>
      {item.interactionData.subject}
    </Text>
  );
}

function TrackerItemCardContent({
  item,
  fields,
}: {
  item: TrackerItem;
  fields: TrackerFieldType[];
}) {
  const visibleColumns = useMemo(() => {
    return fields.map((field) => trackerFieldTypeToExploreColumnType[field]);
  }, [fields]);

  return (
    <Flex direction={"column"} align={"start"} width={"100%"}>
      <Overview
        lite
        source={item.ticketId ? "tickets" : "conversations"}
        interaction={item.interactionData}
        visibleColumns={visibleColumns}
      />
    </Flex>
  );
}

interface TrackerItemProps {
  item: TrackerItem;
  trackerId: string;
  trackerColumns: TrackerColumn[];
  trackerFields: TrackerFieldType[];
  onItemClick: (interactionId: string, intercationType: Sources) => void;
  onItemUpdate: () => void;
}

function TrackerItemCard({
  item,
  trackerId,
  trackerColumns,
  trackerFields,
  onItemClick,
  onItemUpdate,
}: TrackerItemProps) {
  const handleCardClick = useCallback(() => {
    const intercationType = item.ticketId ? "tickets" : "conversations";
    onItemClick(item.interactionData.id, intercationType);
  }, [onItemClick, item?.interactionData?.id, item?.ticketId]);

  const trackerApi = useTrackerApi();

  // this should be in API folder
  const { mutate: updateTrackerItemColumn } = useMutation({
    mutationKey: ["updateTrackerItemColumn", trackerId, item.id],
    mutationFn: (newColumnId: string) =>
      trackerApi.updateTrackerItemColumn(trackerId, item.id, newColumnId),
    onSuccess: () => {
      onItemUpdate();
    },
  });

  const { isDragging, setNodeRef, listeners, transform, attributes } =
    useDraggable({
      id: item.id,
      data: {
        ticketId: item?.interactionData?.id,
      },
    });

  const style = {
    transform: CSS.Translate.toString(transform),
  };

  const channel = useMemo(() => {
    return item.ticketId ? "autonomous_ticket_event" : "autonomous_quackchat";
  }, [item.ticketId]);

  return (
    <motion.div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      style={style}
      className={cx(
        "bg-background rounded-lg cursor-grab active:cursor-grabbing border border-gray-200 hover:border-gray-300 hover:shadow-lg transition-all duration-200 relative",
        {
          "opacity-50 scale-105 rotate-2 shadow-2xl z-50": isDragging,
        }
      )}
      whileHover={{
        y: -2,
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.1)",
      }}
      whileTap={{
        scale: 0.98,
        cursor: "grabbing",
      }}
      initial={{ y: 20 }}
      animate={{ y: 0 }}
      exit={{ opacity: 0, y: -20 }}
      transition={{ duration: 0.2 }}
    >
      <div className="p-4">
        <div className="flex justify-between items-start mb-3">
          <div className="flex items-center gap-2">
            <SimulatorChannelIcon channel={channel} />
            <div className="text-xs text-gray-500 font-medium">
              #{item.interactionData.displayId}
            </div>
          </div>
          <DropdownMenu
            items={trackerColumns
              .filter((column) => column.stage !== item.stage)
              .map((option) => ({
                key: option.stage,
                label: option.name,
                onClick: () => {
                  updateTrackerItemColumn(option.id);
                },
                icon: (
                  <TrackerStageIcon
                    stage={option.stage}
                    width={12}
                    height={16}
                  />
                ),
              }))}
          >
            <DropdownMenu.Trigger>
              <IconButton
                color={"gray"}
                variant={"ghost"}
                highContrast
                size={"1"}
                className="opacity-60 hover:opacity-100 transition-opacity"
              >
                <DotsHorizontalIcon />
              </IconButton>
            </DropdownMenu.Trigger>
          </DropdownMenu>
        </div>

        <div className="cursor-pointer" onClick={handleCardClick}>
          <TrackerItemCardTitle item={item} />
          <div className="mt-2">
            <TrackerItemCardContent item={item} fields={trackerFields} />
          </div>
        </div>

        {isDragging && (
          <motion.div
            className="absolute inset-0 bg-gray-50 border-2 border-gray-300 rounded-lg flex items-center justify-center"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <div className="text-gray-600 font-medium">Moving...</div>
          </motion.div>
        )}
      </div>
    </motion.div>
  );
}

export default TrackerItemCard;
